import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { SuperAdminPageHelp } from '@/components/SuperAdmin/SuperAdminPageHelp';
import { ProjectSearchSelect } from '@/components/ProjectSearchSelect';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import { deviceTypeService } from '@/services/deviceTypeService';
import { areaTypeService } from '@/services/areaTypeService';
import {
  superTopologyDebugApi,
  SuperTopologyDebugAreasDuplicateDevicesResponse,
  SuperTopologyDebugDeviceAttributesLastResponse,
  SuperTopologyDebugResponse,
} from '@/api/superTopologyDebugApi';
import type { AreaType, DeviceType } from '@/types';

type Tab = 'apartments' | 'rooms' | 'boxes' | 'devices' | 'attributes' | 'duplicateDevices';

function isOlderThanTwoMonths(tsSec: number | null): boolean {
  if (!tsSec) return false;
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - 2);
  return tsSec * 1000 < cutoff.getTime();
}

function getErrorMessage(err: unknown): string {
  if (!err) return 'Erreur';
  if (typeof err === 'object' && err !== null && 'body' in err) {
    const body = (err as { body?: unknown }).body;
    if (typeof body === 'object' && body !== null && 'message' in body) {
      const msg = (body as { message?: unknown }).message;
      if (typeof msg === 'string' && msg) return msg;
    }
  }
  if (err instanceof Error) return err.message || 'Erreur';
  return 'Erreur';
}

export function SuperTopologyDebugPage() {
  const { user } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();

  const [activeTab, setActiveTab] = useState<Tab>('apartments');
  const [selectedProjectIri, setSelectedProjectIri] = useState<string>('');
  const [expandedDeviceId, setExpandedDeviceId] = useState<number | null>(null);
  const [selectedDeviceTypeId, setSelectedDeviceTypeId] = useState<number>(0);
  const [selectedAreaTypeId, setSelectedAreaTypeId] = useState<number>(0);

  const platformProjectId = useMemo(() => {
    if (!selectedProjectIri) return null;
    const n = parseInt(selectedProjectIri.replace('/api/projects/', ''), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [selectedProjectIri]);

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const debugQuery = useQuery<SuperTopologyDebugResponse>({
    queryKey: ['superTopologyDebug', platformProjectId],
    enabled: !!platformProjectId,
    queryFn: async () => {
      if (!platformProjectId) throw new Error('Please select a project');
      return superTopologyDebugApi.getOrphans(platformProjectId);
    },
  });

  const data = debugQuery.data;

  const deviceTypesQuery = useQuery<DeviceType[]>({
    queryKey: ['superTopologyDebugDeviceTypes'],
    queryFn: async () => deviceTypeService.getDeviceTypes(),
  });

  const areaTypesQuery = useQuery<AreaType[]>({
    queryKey: ['superTopologyDebugAreaTypes'],
    queryFn: async () => areaTypeService.getAreaTypes(),
  });

  const deviceAttributesQuery = useQuery<SuperTopologyDebugDeviceAttributesLastResponse>({
    queryKey: ['superTopologyDebugDeviceAttributesLast', platformProjectId, expandedDeviceId],
    enabled: !!platformProjectId && !!expandedDeviceId,
    queryFn: async () => {
      if (!platformProjectId || !expandedDeviceId) throw new Error('Missing project/device');
      return superTopologyDebugApi.getDeviceAttributesLast(platformProjectId, expandedDeviceId);
    },
  });

  const duplicateDevicesQuery = useQuery<SuperTopologyDebugAreasDuplicateDevicesResponse>({
    queryKey: ['superTopologyDebugDuplicateDevices', platformProjectId, selectedDeviceTypeId, selectedAreaTypeId],
    enabled: !!platformProjectId && selectedDeviceTypeId > 0 && selectedAreaTypeId > 0,
    queryFn: async () => {
      if (!platformProjectId) throw new Error('Missing project');
      return superTopologyDebugApi.getAreasDuplicateDevices(platformProjectId, selectedDeviceTypeId, selectedAreaTypeId);
    },
  });

  const formatDateTimeFr = (tsSec: number | null) => {
    if (!tsSec) return '—';
    const d = new Date(tsSec * 1000);
    const pad2 = (n: number) => String(n).padStart(2, '0');
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(
      d.getSeconds()
    )}`;
  };

  const attributesRows = useMemo(() => {
    if (!data) return [];
    return data.attributes.map((a) => ({
      ...a,
      lastTimestamp: formatDateTimeFr(a.lastTimestampSec),
    }));
  }, [data]);

  type AttributesRow = (typeof attributesRows)[number];

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: 'apartments', label: 'Appartements', count: data?.apartments.length ?? 0 },
    { id: 'rooms', label: 'Chambres', count: data?.rooms.length ?? 0 },
    { id: 'boxes', label: 'Boxs', count: data?.boxes.length ?? 0 },
    { id: 'devices', label: 'Devices', count: data?.devices.length ?? 0 },
    { id: 'attributes', label: 'Attributes', count: data?.attributes.length ?? 0 },
    { id: 'duplicateDevices', label: 'Doublons devices', count: duplicateDevicesQuery.data?.count ?? 0 },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Super utilisateur</span> / Debug Topology
          </div>
          <div className="flex items-center gap-4">
            <SuperAdminPageHelp />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              <span className="material-icons-outlined">{isDark ? 'light_mode' : 'dark_mode'}</span>
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <span className="material-icons-outlined text-lg">person</span>
              <span>{user?.fullName || 'Admin'}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Debug Topology</h1>

            <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-5 space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Platform Project</label>
                <ProjectSearchSelect value={selectedProjectIri} onChange={setSelectedProjectIri} />
              </div>

              {debugQuery.isFetching && (
                <div className="text-sm text-slate-500 dark:text-slate-400">Chargement...</div>
              )}
              {debugQuery.error && (
                <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {getErrorMessage(debugQuery.error)}
                </div>
              )}
              {data && (
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  generatedAt={data.generatedAt} — limit={data.limit}
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-200 dark:border-[#1E293B] mb-6">
              <nav className="flex space-x-1" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    disabled={!platformProjectId}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      activeTab === tab.id
                        ? 'bg-primary/10 text-primary dark:text-accent border-b-2 border-primary dark:border-accent'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      {tab.label}
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200/70 dark:bg-slate-800">
                        {tab.count}
                      </span>
                    </span>
                  </button>
                ))}
              </nav>
            </div>

            {!platformProjectId && (
              <div className="text-sm text-slate-500 dark:text-slate-400">Sélectionne un projet pour afficher les anomalies.</div>
            )}

            {platformProjectId && data && (
              <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl overflow-hidden">
                {activeTab === 'apartments' && (
                  <Table
                    title="Appartements (0 box / 0 device / 0 attributes)"
                    emptyLabel="Aucun appartement trouvé."
                    columns={[
                      { key: 'id', label: 'ID' },
                      { key: 'name', label: 'Nom' },
                      { key: 'code', label: 'Code' },
                    ]}
                    rows={data.apartments}
                  />
                )}

                {activeTab === 'rooms' && (
                  <Table
                    title="Chambres (0 device / 0 attributes)"
                    emptyLabel="Aucune chambre trouvée."
                    columns={[
                      { key: 'id', label: 'ID' },
                      { key: 'name', label: 'Nom' },
                      { key: 'code', label: 'Code' },
                    ]}
                    rows={data.rooms}
                  />
                )}

                {activeTab === 'boxes' && (
                  <Table
                    title="Boxs (0 area)"
                    emptyLabel="Aucune box trouvée."
                    columns={[
                      { key: 'id', label: 'ID' },
                      { key: 'name', label: 'Nom' },
                      { key: 'code', label: 'Code' },
                    ]}
                    rows={data.boxes}
                  />
                )}

                {activeTab === 'devices' && (
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="font-semibold text-slate-900 dark:text-white">Devices (0 area)</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">rows={data.devices.length}</div>
                    </div>

                    {data.devices.length === 0 ? (
                      <div className="text-sm text-slate-500 dark:text-slate-400">Aucun device trouvé.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50 dark:bg-[#0B1116]">
                            <tr>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                ID
                              </th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                Nom
                              </th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                Code
                              </th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                Box ID
                              </th>
                              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                Attrs
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.devices.map((d) => {
                              const isOpen = expandedDeviceId === d.id;
                              return (
                                <Fragment key={d.id}>
                                  <tr className="border-b border-slate-100 dark:border-slate-800">
                                    <td className="px-4 py-3 whitespace-nowrap">{d.id}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">{d.name}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">{d.code}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">{d.boxId}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-right">
                                      <button
                                        type="button"
                                        onClick={() => setExpandedDeviceId(isOpen ? null : d.id)}
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
                                        aria-label="Afficher les attributes"
                                      >
                                        <span className="material-icons-outlined text-base">
                                          {isOpen ? 'expand_less' : 'expand_more'}
                                        </span>
                                      </button>
                                    </td>
                                  </tr>

                                  {isOpen && (
                                    <tr className="border-b border-slate-100 dark:border-slate-800">
                                      <td colSpan={5} className="px-4 py-4 bg-slate-50/60 dark:bg-[#0B1116]">
                                        {deviceAttributesQuery.isFetching && (
                                          <div className="text-sm text-slate-500 dark:text-slate-400">Chargement attributes...</div>
                                        )}
                                        {deviceAttributesQuery.error && (
                                          <div className="text-sm text-red-700 dark:text-red-300">
                                            {getErrorMessage(deviceAttributesQuery.error)}
                                          </div>
                                        )}
                                        {deviceAttributesQuery.data && (
                                          <div className="overflow-x-auto">
                                            <table className="min-w-full text-sm">
                                              <thead>
                                                <tr>
                                                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                                    Attribute
                                                  </th>
                                                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                                    Type
                                                  </th>
                                                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                                    Last value
                                                  </th>
                                                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                                    Last timestamp
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {deviceAttributesQuery.data.attributes.map((a) => (
                                                  <tr key={a.id} className="border-t border-slate-200/60 dark:border-slate-800">
                                                    <td className="px-3 py-2 whitespace-nowrap">{a.name}</td>
                                                    <td className="px-3 py-2 whitespace-nowrap">{a.attributeTypeCode ?? '—'}</td>
                                                    <td className="px-3 py-2 whitespace-nowrap">{a.lastValue ?? '—'}</td>
                                                    <td
                                                      className={`px-3 py-2 whitespace-nowrap ${
                                                        isOlderThanTwoMonths(a.lastTimestampSec) ? 'text-red-700 dark:text-red-300 font-semibold' : ''
                                                      }`}
                                                    >
                                                      {formatDateTimeFr(a.lastTimestampSec)}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                </Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'attributes' && (
                  <Table
                    title="Attributes (0 area)"
                    emptyLabel="Aucun attribute trouvé."
                    columns={[
                      { key: 'id', label: 'ID' },
                      { key: 'name', label: 'Nom' },
                      { key: 'attributeTypeCode', label: 'Type' },
                      { key: 'deviceName', label: 'Device' },
                      { key: 'lastValue', label: 'Last value' },
                      { key: 'lastTimestamp', label: 'Last timestamp' },
                    ]}
                    rows={attributesRows as AttributesRow[]}
                  />
                )}

                {activeTab === 'duplicateDevices' && (
                  <div className="p-5 space-y-4">
                    <div className="flex flex-col md:flex-row gap-3 md:items-end">
                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                          Device type
                        </label>
                        <select
                          value={selectedDeviceTypeId}
                          onChange={(e) => setSelectedDeviceTypeId(parseInt(e.target.value, 10) || 0)}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-sm"
                          disabled={deviceTypesQuery.isFetching}
                        >
                          <option value={0}>— sélectionner —</option>
                          {(deviceTypesQuery.data ?? [])
                            .slice()
                            .sort((a, b) => (a.type ?? '').localeCompare(b.type ?? ''))
                            .map((dt) => (
                              <option key={dt.id} value={dt.id}>
                                {dt.type}
                              </option>
                            ))}
                        </select>
                      </div>

                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                          Area type
                        </label>
                        <select
                          value={selectedAreaTypeId}
                          onChange={(e) => setSelectedAreaTypeId(parseInt(e.target.value, 10) || 0)}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-sm"
                          disabled={areaTypesQuery.isFetching}
                        >
                          <option value={0}>— sélectionner —</option>
                          {(areaTypesQuery.data ?? [])
                            .slice()
                            .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''))
                            .map((at) => (
                              <option key={at.id} value={at.id}>
                                {at.title} ({at.code})
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    {duplicateDevicesQuery.isFetching && (
                      <div className="text-sm text-slate-500 dark:text-slate-400">Analyse en cours...</div>
                    )}
                    {duplicateDevicesQuery.error && (
                      <div className="text-sm text-red-700 dark:text-red-300">
                        {getErrorMessage(duplicateDevicesQuery.error)}
                      </div>
                    )}

                    {selectedDeviceTypeId === 0 || selectedAreaTypeId === 0 ? (
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        Sélectionne un type de device + un type d’area.
                      </div>
                    ) : (
                      duplicateDevicesQuery.data && (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 dark:bg-[#0B1116]">
                              <tr>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                  Area
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                  Code
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                  Type
                                </th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                  Nb devices
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                  Devices
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {duplicateDevicesQuery.data.areas.map((a) => (
                                <tr key={a.id} className="border-b border-slate-100 dark:border-slate-800">
                                  <td className="px-4 py-3 whitespace-nowrap">{a.name}</td>
                                  <td className="px-4 py-3 whitespace-nowrap">{a.code}</td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    {a.areaTypeTitle ?? '—'} {a.areaTypeCode ? `(${a.areaTypeCode})` : ''}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-right font-semibold">{a.deviceCount}</td>
                                  <td className="px-4 py-3 text-xs break-all">{a.devices || '—'}</td>
                                </tr>
                              ))}
                              {duplicateDevicesQuery.data.areas.length === 0 && (
                                <tr>
                                  <td colSpan={5} className="px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
                                    Aucun doublon trouvé.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

type ColumnDef<T extends Record<string, unknown>> = { key: keyof T; label: string };

function Table<T extends Record<string, unknown>>({
  title,
  emptyLabel,
  columns,
  rows,
}: {
  title: string;
  emptyLabel: string;
  columns: ColumnDef<T>[];
  rows: T[];
}) {
  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="font-semibold text-slate-900 dark:text-white">{title}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">rows={rows.length}</div>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-slate-500 dark:text-slate-400">{emptyLabel}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 dark:bg-[#0B1116]">
              <tr>
                {columns.map((c) => (
                  <th
                    key={String(c.key)}
                    className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]"
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} className="border-b border-slate-100 dark:border-slate-800">
                  {columns.map((c) => {
                    const val = r[c.key];
                    const isStaleTimestamp =
                      String(c.key) === 'lastTimestamp' &&
                      typeof (r as unknown as { lastTimestampSec?: unknown }).lastTimestampSec === 'number' &&
                      isOlderThanTwoMonths((r as unknown as { lastTimestampSec: number }).lastTimestampSec);
                    return (
                      <td
                        key={String(c.key)}
                        className={`px-4 py-3 text-slate-700 dark:text-slate-200 whitespace-nowrap ${
                          isStaleTimestamp ? 'text-red-700 dark:text-red-300 font-semibold' : ''
                        }`}
                      >
                        {val === null || val === undefined ? (
                          <span className="text-slate-400 dark:text-slate-500">—</span>
                        ) : (
                          String(val)
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

